import React, { useState, useEffect, useCallback } from 'react';
import { attendance as attendanceApi, attendanceList } from '../../api/endpoints';
import { getList, getTotal } from '../../api/client';
import { useFetchList, useNotification, useOpenRecordFromLink, fetchRecord, RECORD_GONE } from '../../hooks';
import { getPreferences } from '../../utils/preferences';
import { optionListDefaults } from '../../utils/optionLists';
import { formatDate, formatTime, parseTime, to12h } from '../../utils/format';
import useStyles from '../../theme/useStyles';
import {
  PageHeader, SearchBar, DateRangeFilter, DataTable, Pagination, Notification,
  PromptDialog, ConfirmDialog, FormLayout, Input, Select, SearchableSelect,
  Badge, Button, Drawer, RowActions,
  TimeField, PrintButton
} from '../../components/ui';
import type { AttendanceRecord } from '../../types';
import type { Column } from '../../components/ui';
import AttendanceSummary from './AttendanceSummary';

// The filter's event names start from the same list the form's dropdown uses.
const DEFAULT_EVENT_TYPES = optionListDefaults('church_attendance_events');

type AttendanceForm = Pick<AttendanceRecord,
  'date' | 'serviceTime' | 'eventName' | 'venue' | 'attendees' |
  'menCount' | 'womenCount' | 'youthCount' | 'childrenCount' | 'newVisitors' | 'notes'>;

const EMPTY_FORM: AttendanceForm = {
  date: '', serviceTime: '', eventName: '', venue: '', attendees: '',
  menCount: '', womenCount: '', youthCount: '', childrenCount: '', newVisitors: '', notes: ''
};

const num = (v: unknown): number => {
  const n = parseInt(String(v ?? '').replace(/[^0-9]/g, ''), 10);
  return Number.isFinite(n) ? n : 0;
};
const today = () => new Date().toISOString().slice(0, 10);
const stamp = () => new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

/** Debounce a value — holds the search term steady between keystrokes. */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const AttendanceManagement = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;
  // The Summary opens first; the log is one tab away.
  const [activeTab, setActiveTab] = useState('summary');
  const [formData, setFormData] = useState<AttendanceForm>(EMPTY_FORM);
  const [editingRecord, setEditingRecord] = useState<AttendanceRecord | null>(null);
  const [viewingRecord, setViewingRecord] = useState<AttendanceRecord | null>(null);
  const [previewRecord, setPreviewRecord] = useState<AttendanceRecord | null>(null);
  /** True while the print dialog is being prepared, so the Actions column goes. */
  const [printing, setPrinting] = useState(false);
  /**
   * The table has to be in its printed shape before the dialog snapshots the
   * page: two frames for React to commit and paint, then print, then put the
   * screen back when the dialog closes.
   */
  const [pendingDuplicate, setPendingDuplicate] = useState<Partial<AttendanceRecord> | null>(null);
  const [pendingVoid, setPendingVoid] = useState<AttendanceRecord | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const [eventFilter, setEventFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { notification, notify, dismiss } = useNotification();

  const isDeletedView = activeTab === 'deleted';
  const isListView = activeTab === 'ledger' || isDeletedView;
  const bucket = isDeletedView ? 'deleted' : 'active';

  const PAGE_SIZE = getPreferences().pageSize;
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm.trim(), 300);
  const [pageNum, setPageNum] = useState(1);

  useEffect(() => { setPageNum(1); }, [activeTab, debouncedSearch, eventFilter, startDate, endDate]);

  // Server-paged log — the browser fetches only the current page.
  const { data: rows, raw: pageRaw, loading, refetch: refetchPage } =
    useFetchList<AttendanceRecord>((p, o) => attendanceList.page(p, o), {
      params: { bucket, q: debouncedSearch, event: eventFilter, from: startDate, to: endDate, page: pageNum - 1, size: PAGE_SIZE },
      enabled: isListView
    });
  const totalItems = getTotal(pageRaw);
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  const [counts, setCounts] = useState({ active: 0, deleted: 0 });
  const refetchCounts = useCallback(async () => {
    try {
      const c = await attendanceList.counts();
      setCounts({ active: Number(c.active) || 0, deleted: Number(c.deleted) || 0 });
    } catch { /* keep last */ }
  }, []);
  useEffect(() => { refetchCounts(); }, [refetchCounts]);

  // The filter offers every event name ever recorded plus the defaults. It is
  // reloaded after each save, so a type introduced by a new record appears in
  // the filter without a page reload (CHM-015).
  const [eventOptions, setEventOptions] = useState<string[]>([...DEFAULT_EVENT_TYPES]);
  const refetchEventOptions = useCallback(() => {
    attendanceList.events()
      .then((list) => setEventOptions(Array.from(new Set([...DEFAULT_EVENT_TYPES, ...(list || [])])).sort()))
      .catch(() => { /* keep defaults */ });
  }, []);
  useEffect(() => { refetchEventOptions(); }, [refetchEventOptions]);

  // The Summary works over every active record: one page as large as the log
  // (the rows are small — one per service), voided records left out server-side.
  const [allRecords, setAllRecords] = useState<AttendanceRecord[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const refetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try { setAllRecords(getList<AttendanceRecord>(await attendanceList.page({ bucket: 'active', page: 0, size: 100000 }))); }
    catch { setAllRecords([]); }
    finally { setSummaryLoading(false); }
  }, []);
  useEffect(() => { if (activeTab === 'summary') refetchSummary(); }, [activeTab, refetchSummary]);

  const refetch = useCallback(async () => {
    await Promise.all([refetchPage(), refetchCounts(), refetchSummary()]);
  }, [refetchPage, refetchCounts, refetchSummary]);

  const page = {
    currentItems: rows, currentPage: pageNum, totalPages, totalItems, itemsPerPage: PAGE_SIZE,
    setCurrentPage: setPageNum,
    next: () => setPageNum((p) => Math.min(p + 1, totalPages)),
    prev: () => setPageNum((p) => Math.max(p - 1, 1)),
    isFirst: pageNum <= 1, isLast: pageNum >= totalPages, reset: () => setPageNum(1)
  };

  const clearRange = () => { setStartDate(''); setEndDate(''); };
  const rangeActive = Boolean(startDate || endDate);

  const resetForm = () => { setFormData(EMPTY_FORM); setEditingRecord(null); setErrors({}); };

  const switchTab = (key) => {
    if (key !== 'add') resetForm();
    setActiveTab(key);
    setSearchTerm('');
    setPageNum(1);
  };

  // "Open the record" from the Approvals inbox: its list (or Deleted History),
  // then its details.
  useOpenRecordFromLink(async ({ id, ref }) => {
    const found = await fetchRecord(id, ref, { attendance: attendanceApi.get });
    if (!found) { notify(RECORD_GONE, 'error'); return; }
    const r = found.record as AttendanceRecord;
    switchTab((r as { status?: string }).status === 'VOID' ? 'deleted' : 'ledger');
    setViewingRecord(r);
  });

  const startEdit = (record: AttendanceRecord) => {
    setEditingRecord(record);
    setFormData({
      date: record.date || '', serviceTime: record.serviceTime || '',
      eventName: record.eventName || '', venue: record.venue || '',
      attendees: record.attendees ?? '', menCount: record.menCount ?? '',
      womenCount: record.womenCount ?? '', youthCount: record.youthCount ?? '',
      childrenCount: record.childrenCount ?? '', newVisitors: record.newVisitors ?? '',
      notes: record.notes || ''
    });
    setErrors({});
    setViewingRecord(null);
    setActiveTab('add');
  };

  /** Editing a breakdown field re-totals the headcount automatically. */
  const setBreakdown = (field: keyof AttendanceForm) => (e) => {
    const value = e.target.value;
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      const sum = num(next.menCount) + num(next.womenCount) + num(next.youthCount) + num(next.childrenCount);
      return { ...next, attendees: String(sum) };
    });
  };

  const setField = (field: keyof AttendanceForm) => (e) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const doSave = async (payload: Partial<AttendanceRecord>) => {
    setSaving(true);
    try {
      if (editingRecord) await attendanceApi.update(editingRecord.id, payload);
      else await attendanceApi.create(payload);
      await refetch();
      refetchEventOptions();
      resetForm();
      setActiveTab('ledger');
      notify(editingRecord ? 'Attendance updated.' : 'Attendance saved.', 'success');
    } catch (err) {
      notify((err as Error)?.message || 'Could not save the attendance.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const found: Record<string, string> = {};
    if (!formData.eventName?.trim()) found.eventName = 'Choose the event / service.';
    if (!parseTime(formData.serviceTime)) found.serviceTime = 'Pick the service time.';
    if (!formData.date?.trim()) found.date = 'The service date is required.';
    else if (formData.date > today()) found.date = 'The date cannot be in the future.';
    if (num(formData.attendees) <= 0) found.attendees = 'Enter the total headcount.';
    if (Object.keys(found).length) {
      setErrors(found);
      notify('Please correct the highlighted fields.', 'error');
      return;
    }
    setErrors({});

    // Editing keeps the record's id / recordId / status (spread first); a new
    // record is created with no recordId — the backend generates ATT-<id>.
    // Stored in the one form the register reads, whatever the field was given.
    const serviceTime = to12h(parseTime(formData.serviceTime)!);
    const payload: Partial<AttendanceRecord> = editingRecord
      ? { ...editingRecord, ...formData, serviceTime }
      : { ...formData, serviceTime, status: 'ACTIVE' as AttendanceRecord['status'] };

    // Duplicate warning on a new record: ask the server if the same event is
    // already logged for that date (one targeted call, not the whole table).
    if (!editingRecord) {
      const dup = await attendanceList.page({ bucket: 'active', event: formData.eventName, from: formData.date, to: formData.date, page: 0, size: 1 }).catch(() => null);
      if (dup && getTotal(dup) > 0) { setPendingDuplicate(payload); return; }
    }
    await doSave(payload);
  };

  const confirmVoid = async (reason: string) => {
    const record = pendingVoid;
    setPendingVoid(null);
    if (!record) return false;
    try {
      await attendanceApi.update(record.id, { ...record, status: 'VOID', deleteReason: reason, deletedAt: stamp() } as Partial<AttendanceRecord>);
      await refetch();
      notify('Record deleted. Kept in Deleted History.', 'success');
      return true;
    } catch (err) {
      notify((err as Error)?.message || 'Could not delete the record.', 'error');
      return false;
    }
  };

  const handleRestore = async (record: AttendanceRecord) => {
    try {
      await attendanceApi.update(record.id, { ...record, status: 'ACTIVE', deleteReason: null, deletedAt: null } as Partial<AttendanceRecord>);
      await refetch();
      notify(`Record ${record.recordId} restored.`, 'success');
    } catch (err) {
      notify((err as Error)?.message || 'Could not restore the record.', 'error');
    }
  };


  const baseColumns: Array<Column<AttendanceRecord>> = [
    { key: 'recordId', header: 'Record ID', nowrap: true, render: (r) => <strong>{r.recordId}</strong> },
    { key: 'date', header: 'Date', nowrap: true, render: (r) => formatDate(r.date) },
    { key: 'serviceTime', header: 'Time', nowrap: true, render: (r) => formatTime(r.serviceTime) },
    { key: 'eventName', header: 'Event Name', render: (r) => <Badge>{r.eventName}</Badge> },
    { key: 'venue', header: 'Venue', render: (r) => r.venue || '-' },
    {
      key: 'attendees', header: 'Total', align: 'center',
      render: (r) => <span style={{ fontWeight: 'bold', fontSize: '16px', color: t.textMain }}>{r.attendees || 0}</span>
    }
  ];

  const actionsColumn: Column<AttendanceRecord> = {
    key: 'actions', header: 'Actions', align: 'right', nowrap: true,
    render: (r) => (
      <span onClick={(e) => e.stopPropagation()}>
        <RowActions
          isDarkMode={isDarkMode}
          items={isDeletedView
            ? [{ icon: '♻️', label: 'Restore', onClick: () => handleRestore(r) }]
            : [
                { icon: '👁️', label: 'View', onClick: () => setViewingRecord(r) },
                { icon: '✏️', label: 'Edit', write: true, onClick: () => startEdit(r) },
                { icon: '🗑️', label: 'Delete', separated: true, danger: true, onClick: () => setPendingVoid(r) }
              ]}
        />
      </span>
    )
  };

  /** What the printout is of — the filters in force, for the masthead line. */
  const rangeLabel = [
    eventFilter || 'All events',
    startDate || endDate ? `${startDate ? formatDate(startDate) : 'start'} to ${endDate ? formatDate(endDate) : 'today'}` : null,
    debouncedSearch ? `search: ${debouncedSearch}` : null
  ].filter(Boolean).join('  •  ');

  const columns: Array<Column<AttendanceRecord>> = isDeletedView
    ? [
        ...baseColumns,
        { key: 'deletedAt', header: 'Deleted On', render: (r) => <span style={{ fontSize: '12px', color: t.textFaint }}>{r.deletedAt || '-'}</span> },
        { key: 'deleteReason', header: 'Reason', wrap: true, render: (r) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{r.deleteReason || '-'}</span> },
        actionsColumn
      ]
    : [...baseColumns, actionsColumn];

  // --- Full-screen single-record print sheet ---
  if (previewRecord) {
    const r = previewRecord;
    const detail = (label: string, value) => (
      <tr>
        <td style={{ padding: '8px 12px', fontWeight: 'bold', width: '40%', borderBottom: '1px solid #ddd' }}>{label}</td>
        <td style={{ padding: '8px 12px', borderBottom: '1px solid #ddd' }}>{value || '-'}</td>
      </tr>
    );
    return (
      <div style={styles.container}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '20px' }}>
          <Button variant="secondary" onClick={() => setPreviewRecord(null)}>← Back to List</Button>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button variant="ghost" onClick={() => { const rec = r; setPreviewRecord(null); startEdit(rec); }}>✏️ Edit</Button>
          </div>
        </div>
        <div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <tbody>
              {detail('Record ID', r.recordId)}
              {detail('Date', formatDate(r.date))}
              {detail('Service Time', formatTime(r.serviceTime))}
              {detail('Event / Service', r.eventName)}
              {detail('Venue', r.venue)}
              {detail('Men', r.menCount)}
              {detail('Women', r.womenCount)}
              {detail('Youth', r.youthCount)}
              {detail('Children', r.childrenCount)}
              {detail('New Visitors (first-timers)', r.newVisitors)}
              {detail('Total Attendance', r.attendees)}
              {detail('Notes', r.notes)}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        icon="📅"
        title="Attendance Management"
        activeTab={activeTab}
        onTabChange={switchTab}
        tabs={[
          { key: 'summary', label: '📊 Summary' },
          { key: 'ledger', label: `Attendance Log (${counts.active})` },
          { key: 'deleted', label: `🗑️ Deleted History (${counts.deleted})`, variant: 'danger' },
          { key: 'add', write: true, label: editingRecord ? '✏️ Editing…' : '+ Add New Entry' }
        ]}
      />

      {isListView && (
        <>
          {/* One row, as on Members & Families: a compact search field, the
              date range and event filters beside it, and the export/print
              buttons at the far right. Wraps on a narrow screen. */}
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onClear={() => setSearchTerm('')}
            placeholder="Search by Record ID, Event or Venue..."
            fieldStyle={{ flex: '0 1 300px', minWidth: '200px' }}
            style={{ flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}
            actions={
              <>
                <PrintButton
                  module="Attendance"
                  documentType="ATTENDANCE_REGISTER"
                  size="sm"
                  title="Attendance-Register"
                  data={() => ({
                    title: 'Attendance Register',
                    reference: rangeLabel,
                    rowCount: page.currentItems.length,
                    // The module's own records, untouched: a template names the
                    // fields this register actually stores.
                    rows: page.currentItems
                  })}
                  onNotify={notify}
                  isDarkMode={isDarkMode}
                />
                <DateRangeFilter
                  startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate}
                  clear={clearRange} isActive={rangeActive} onChange={() => setPageNum(1)} isDarkMode={isDarkMode}
                />
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ fontSize: t.fontSize.base, color: t.textMuted, fontWeight: 'bold' }}>Event:</span>
                  <Select
                    value={eventFilter}
                    onChange={(e) => setEventFilter(e.target.value)}
                    options={eventOptions}
                    placeholder="All events"
                    containerStyle={{ margin: 0 }}
                    style={{ margin: 0, width: 'auto' }}
                    isDarkMode={isDarkMode}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginLeft: 'auto' }}>
                </div>
              </>
            }
          />

          {/* The register prints through the shared print surface, so it
              carries the parish's chosen template and nothing of the app —
              this block used to print the whole screen, chrome and all. */}
          <div>
            <DataTable
              columns={printing ? columns.filter((c) => c.key !== 'actions') : columns}
              rows={page.currentItems}
              loading={loading}
              onRowClick={(r) => (isDeletedView ? undefined : setViewingRecord(r))}
              emptyMessage="No attendance records found."
            />
          </div>

          <Pagination {...page} itemsOnPage={page.currentItems.length} label="records" />
        </>
      )}

      {activeTab === 'summary' && (
        <AttendanceSummary records={allRecords} loading={summaryLoading} isDarkMode={isDarkMode} />
      )}

      {activeTab === 'add' && (
        <FormLayout
          title={editingRecord ? 'Edit Attendance Record' : 'Record Service Attendance'}
          submitLabel={editingRecord ? 'Update Attendance Log' : 'Save Attendance Log'}
          submitting={saving}
          onSubmit={handleSubmit}
          maxWidth="820px"
          onCancel={editingRecord ? () => switchTab('ledger') : undefined}
        >
          <FormLayout.Grid>
            <Input label="Service Date" name="date" type="date" required value={formData.date} error={errors.date} max={today()} onChange={setField('date')} />
            {/* 12-hour picker — hour / minute / AM-PM columns; stored as "hh:mm AM/PM" (CHM-012). */}
            <TimeField
              label="Service Time" name="serviceTime" required
              value={formData.serviceTime} error={errors.serviceTime}
              onChange={(v) => { setFormData((prev) => ({ ...prev, serviceTime: v })); setErrors((e) => ({ ...e, serviceTime: undefined })); }}
              hint="e.g. 08:30 AM"
            />
            <SearchableSelect
              label="Event / Service Type" required
              value={formData.eventName}
              onChange={(v) => { setFormData((prev) => ({ ...prev, eventName: v })); setErrors((e) => ({ ...e, eventName: undefined })); }}
              storageKey="church_attendance_events"  error={errors.eventName}
            />
            <Input label="Venue / Location" name="venue" placeholder="Main Sanctuary" value={formData.venue} onChange={setField('venue')} />
          </FormLayout.Grid>

          <FormLayout.Section title="Breakdown (optional — the total adds up automatically)" />
          <FormLayout.Grid>
            <Input label="Men" name="menCount" type="number" min="0" value={formData.menCount} onChange={setBreakdown('menCount')} />
            <Input label="Women" name="womenCount" type="number" min="0" value={formData.womenCount} onChange={setBreakdown('womenCount')} />
            <Input label="Youth" name="youthCount" type="number" min="0" value={formData.youthCount} onChange={setBreakdown('youthCount')} />
            <Input label="Children" name="childrenCount" type="number" min="0" value={formData.childrenCount} onChange={setBreakdown('childrenCount')} />
            <Input label="New Visitors (first-timers)" name="newVisitors" type="number" min="0" value={formData.newVisitors} onChange={setField('newVisitors')} />
          </FormLayout.Grid>

          <FormLayout.Grid>
            <Input
              label="Total Headcount" name="attendees" type="number" min="0" required placeholder="0"
              value={formData.attendees} error={errors.attendees} onChange={setField('attendees')}
              hint="Auto-filled from the breakdown above; you can still type it in."
              style={{ fontSize: '20px', fontWeight: 'bold', color: t.primaryText, textAlign: 'center' }}
            />
            <Input label="Notes / Remarks" name="notes" fullWidth placeholder="Guest speaker, special service, weather…" value={formData.notes} onChange={setField('notes')} />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {viewingRecord && (
        <Drawer
          open
          title={viewingRecord.eventName}
          subtitle={`${viewingRecord.recordId} · ${formatDate(viewingRecord.date)}`}
          onClose={() => setViewingRecord(null)}
          isDarkMode={isDarkMode}
          footer={
            <div style={{ display: 'flex', gap: '10px' }}>
              <Button write variant="secondary" onClick={() => startEdit(viewingRecord)}>✏️ Edit</Button>
            </div>
          }
        >
          <Drawer.Section title="Service" isDarkMode={isDarkMode} />
          <Drawer.Row label="Date" value={formatDate(viewingRecord.date)} isDarkMode={isDarkMode} />
          <Drawer.Row label="Time" value={formatTime(viewingRecord.serviceTime)} isDarkMode={isDarkMode} />
          <Drawer.Row label="Event" value={viewingRecord.eventName} isDarkMode={isDarkMode} />
          <Drawer.Row label="Venue" value={viewingRecord.venue} isDarkMode={isDarkMode} />

          <Drawer.Section title="Attendance" isDarkMode={isDarkMode} />
          <Drawer.Row label="Men" value={viewingRecord.menCount} isDarkMode={isDarkMode} />
          <Drawer.Row label="Women" value={viewingRecord.womenCount} isDarkMode={isDarkMode} />
          <Drawer.Row label="Youth" value={viewingRecord.youthCount} isDarkMode={isDarkMode} />
          <Drawer.Row label="Children" value={viewingRecord.childrenCount} isDarkMode={isDarkMode} />
          <Drawer.Row label="New Visitors" value={viewingRecord.newVisitors} isDarkMode={isDarkMode} />
          <Drawer.Row label="Total" isDarkMode={isDarkMode}>
            <strong style={{ fontSize: '18px', color: t.textMain }}>{viewingRecord.attendees || 0}</strong>
          </Drawer.Row>

          {viewingRecord.createdBy && <Drawer.Row label="Created By" value={viewingRecord.createdBy} isDarkMode={isDarkMode} />}
          {viewingRecord.notes && (
            <>
              <Drawer.Section title="Notes" isDarkMode={isDarkMode} />
              <Drawer.Row value={viewingRecord.notes} isDarkMode={isDarkMode} />
            </>
          )}
        </Drawer>
      )}

      <ConfirmDialog
        open={Boolean(pendingDuplicate)}
        title="Possible duplicate"
        message={pendingDuplicate
          ? `A record for "${pendingDuplicate.eventName}" on ${formatDate(pendingDuplicate.date)} already exists. Save this one anyway?`
          : ''}
        confirmLabel="Save Anyway"
        destructive={false}
        onConfirm={() => { const p = pendingDuplicate; setPendingDuplicate(null); if (p) doSave(p); }}
        onCancel={() => setPendingDuplicate(null)}
      />

      <PromptDialog
        open={Boolean(pendingVoid)}
        title={`Delete Record ${pendingVoid?.recordId || ''}`}
        message="Please enter the reason for deleting this record. It will be kept in Deleted History."
        placeholder="Reason for deletion"
        confirmLabel="Delete Record"
        requiredError="A reason is required to remove a record."
        onSubmit={confirmVoid}
        onCancel={() => setPendingVoid(null)}
      />
    </div>
  );
};

export default AttendanceManagement;
